import {formatDuration, intervalToDuration} from 'date-fns';
import {formatInTimeZone} from 'date-fns-tz';
import {FlexRow} from '@forecast-it/design-system';
import React, {ReactElement, useState} from 'react';
import {CalendarEventType} from './CalendarEventsFetch';
import {CalendarEventColorBar, CalendarEventStyled, EventDuration, EventName, TimeInfo} from './CalendarEvent_styled';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {CalendarEventSize, getCalendarEventSize} from './CalendarEventUtil';
import {triggerUserPilotNTimes} from '../../../../shared/util/UserPilotUtil';
import {USER_PILOT_FLOW_GCAL_INTRO} from '../../../../shared/util/UserPilotConstants';

const formatDistanceLocale = {
	xSeconds: '{{count}}s',
	xMinutes: '{{count}}m',
	xHours: '{{count}}h',
	xDays: '{{count}}d',
};
const shortEnLocale = {formatDistance: (token, count) => formatDistanceLocale[token].replace('{{count}}', count)};

const formatEventTimeInfo = (event: CalendarEventType) => {
	if (!event?.start || !event?.end || !event?.calendarTimeZone) {
		return '';
	}
	const start = new Date(event.start);
	const end = new Date(event.end);
	const duration = intervalToDuration({
		start,
		end,
	});
	const durationFormatted = formatDuration(duration, {
		format: ['days', 'hours', 'minutes', 'seconds'],
		locale: shortEnLocale,
	});
	const startFormatted = formatInTimeZone(start, event.calendarTimeZone, 'HH:mm');
	return (
		<>
			<div>{startFormatted}</div>
			<EventDuration>{durationFormatted}</EventDuration>
		</>
	);
};

export type CalendarEventProps = {
	event: CalendarEventType;
	isConverted: boolean;
	isLocked: boolean;
	openModal: (calendarEvent: CalendarEventType) => void;
	showContextMenu: (mouseEvent: MouseEvent, calendarEvent: CalendarEventType) => void;
};

export const CalendarEvent = ({event, isLocked, openModal, showContextMenu, isConverted}: CalendarEventProps): ReactElement => {
	const eventTimeInfo = formatEventTimeInfo(event);
	const [fadeOutRight, setFadeOutRight] = useState(false);
	const handleEventContextMenu = mouseEvent => {
		mouseEvent.preventDefault();
		trackEvent('Timesheet Calendar Event Context Menu', 'Viewed');
		showContextMenu(mouseEvent, event);
	};

	const onClick = e => {
		e.stopPropagation();
		trackEvent('Timesheet Calendar Event', 'Clicked', {eventStart: event?.start, eventEnd: event?.end});
		openModal(event);
		setFadeOutRight(true);
		triggerUserPilotNTimes(USER_PILOT_FLOW_GCAL_INTRO, 3);
	};

	const getEventNameAndTime = (size: CalendarEventSize) => {
		return (
			<>
				<EventName isLocked={isLocked} isConverted={isConverted} size={size}>
					{event?.name}
				</EventName>
				<TimeInfo isLocked={isLocked} isConverted={isConverted}>
					{eventTimeInfo}
				</TimeInfo>
			</>
		);
	};

	const getEventLayout = (size: CalendarEventSize) => {
		return (
			<CalendarEventStyled
				onClick={onClick}
				onContextMenu={handleEventContextMenu}
				fadeOutRight={fadeOutRight}
				onAnimationEnd={() => setFadeOutRight(false)}
			>
				<FlexRow alignItems="flex-start">
					<CalendarEventColorBar color={event?.backgroundColor || undefined} size={size} />
					{getEventNameAndTime(size)}
				</FlexRow>
			</CalendarEventStyled>
		);
	};
	return getEventLayout(getCalendarEventSize(event));
};
